import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Archive, MoreVertical, Loader2 } from 'lucide-react';
import { API, Storage } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import moment from 'moment';
import * as utils from '../../../application/common/Utilities';
import getLocalDateTime from '../../../application/routes/games/games-helpers/getLocalDateTime';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { orgMemberQuery } from '../../../application/routes/rewards/rewardQueries';

const gql: any = gqlTag;

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    imageName?: string;
    name: string;
  };
  appSettings: {
    labels: any;
    dateFormat: string;
  };
};

interface Reward {
  id: string;
  name: string;
  imageName: string;
  imageType: string;
  cost: number;
  limitCount: number;
  remainingCount: number;
  personLimit: number;
  ownerType: string;
  validFrom: string;
  validTo: string;
  description: string;
  rewardType: string;
  value: number;
  valueType: string;
  costType: string;
  approverType: string;
  location: string;
  createdBy: string;
  myCirclePoints: number;
  purchaseCount: number;
  isVisible: boolean;
  remaingDaysAvailable: number;
  daysBeforeAvailable: number;
  secondsBeforeStart: number;
  remainingSecondsInMission: number;
}

interface Circle {
  name: string;
  circlePoints: number;
  id: string;
  isActive: boolean;
  lifetimeCirclePoints: number;
  memberType: string;
  status: string;
  rewards: Reward[];
}

function RewardCard({
  reward,
  onEdit,
  canEdit,
  onClick,
}: {
  reward: Reward;
  onEdit: (id: string) => void;
  canEdit: boolean;
  onClick: () => void;
}) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const shouldLock = reward.ownerType === 'mission';

  useEffect(() => {
    async function loadImage() {
      if (reward.imageName && reward.imageName !== '') {
        if (
          reward.imageType === 'gif_url' ||
          reward.imageType === 'giftcard_url' ||
          reward.imageType === 'trophy'
        ) {
          setImageUrl(reward.imageName);
        } else {
          const url = await Storage.get(reward.imageName);
          setImageUrl(url as string);
        }
      }
    }
    loadImage();
  }, [reward.imageName, reward.imageType]);

  return (
    <div
      className={cn(
        'relative rounded-lg border border-border overflow-hidden transition-shadow hover:shadow-md',
        shouldLock ? 'cursor-default' : 'cursor-pointer'
      )}
      onClick={onClick}
    >
      {canEdit && (
        <div
          className="absolute top-2 right-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 bg-background/80 backdrop-blur-sm"
              >
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(reward.id)}>
                Edit
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="flex justify-center items-center h-[200px] bg-muted/30">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={reward.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <p className="text-sm font-bold text-muted-foreground">
            No Image Available
          </p>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold text-foreground truncate">
            {reward.name}
          </p>
          {shouldLock && (
            <Badge variant="secondary" className="text-xs shrink-0">
              Locked
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground font-medium mt-1">
          {formatPoints(reward.cost)}
        </p>
      </div>
    </div>
  );
}

export function RewardsPage() {
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const showAdd = checkPermission(PermissionKeys.GamesRewards_CreateReward);
  const hasUpdatePermission = checkPermission(
    PermissionKeys.GamesRewards_UpdateReward
  );

  const [myRewards, setMyRewards] = useState<Circle[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMyRewards = useCallback(async () => {
    try {
      const startBefore = new Date();

      const orgMemberQueryRes: any = await API.graphql({
        query: orgMemberQuery as any,
        variables: {
          userID: global.me.id,
          orgID: global.me.orgID,
          limit: 100000,
        },
      });

      const orgPoints =
        orgMemberQueryRes.data.OrgMemberByUser.items[0].orgPoints;
      const lifeTimeOrgPoints =
        orgMemberQueryRes.data.OrgMemberByUser.items[0].lifetimeOrgPoints;

      const myQuery = gql`
        query MyQuery(
          $orgID: ID = "smarthaven"
          $today: String = "2024-05-07"
          $today: String = "2024-05-07"
          $nextToken: String = null
          $startBefore: String
        ) {
          listRewardsByOrgName(
            orgID: $orgID
            filter: {
              isDeleted: { eq: false }
              launchDate: { le: $startBefore }
              validTo: { ge: $today }
            }
            nextToken: $nextToken
          ) {
            items {
              approverType
              cost
              costType
              createdAt
              createdBy
              description
              giftcardDisclaimer
              giftcardValue
              id
              imageName
              imageType
              isActive
              isDeleted
              lastUpdatedBy
              launchDate
              limitCount
              location
              maxGiftcardAmount
              minGiftcardAmount
              name
              nextExpireFeedBlast
              nextLaunchFeedBlast
              orgID
              ownerID
              ownerType
              permissions
              personLimit
              remainingCount
              rewardType
              tags
              tangoAccountID
              updatedAt
              utid
              validFrom
              validTo
              value
              valueType
            }
            nextToken
          }
        }
      `;

      let ourRewards: Reward[] = [];
      let nextToken: string | null = null;

      do {
        const listRewardsRes: any = await API.graphql({
          query: myQuery,
          variables: {
            orgID: global.me.orgID,
            startBefore,
            nextToken,
          },
        });

        const rewards = listRewardsRes.data.listRewardsByOrgName.items;

        for (const reward of rewards) {
          if (!reward.isDeleted) {
            const now = getLocalDateTime();
            const validFrom = new Date(reward.validFrom);
            const gmtValidToDate = new Date(reward.validTo);
            const validTo = moment(gmtValidToDate)
              .add(1, 'day')
              .subtract(1, 'second');

            reward.remaingDaysAvailable = utils.daysBetween(now, validTo);
            reward.daysBeforeAvailable = utils.daysBetween(now, validFrom);
            reward.secondsBeforeStart = utils.secondsBetween(now, validFrom);
            reward.remainingSecondsInMission = utils.secondsBetween(
              now,
              validTo
            );

            reward.myCirclePoints = orgPoints;
            reward.purchaseCount = 0;
            reward.isVisible = true;
            if (reward.isVisible) {
              ourRewards.push(reward);
            }
          }
        }

        ourRewards = ourRewards.filter(
          (reward) => reward.remaingDaysAvailable > 0
        );

        nextToken = listRewardsRes.data.listRewardsByOrgName.nextToken;
      } while (nextToken !== null);

      const ourCircles: Circle[] = [
        {
          name: '',
          circlePoints: orgPoints,
          id: '',
          isActive: true,
          lifetimeCirclePoints: lifeTimeOrgPoints,
          memberType: '',
          status: '',
          rewards: ourRewards,
        },
      ];

      setMyRewards(ourCircles);
    } catch (err) {
      console.error('fetchMyRewards(): error fetching my Catalog' + err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyRewards();
  }, [fetchMyRewards]);

  const handleEditReward = (rewardId: string) => {
    navigate(`/v2/performance/rewards/edit/${rewardId}`);
  };

  const handleRewardClick = (reward: Reward) => {
    navigate('/v2/performance/rewards/details', { state: reward });
  };

  const headerActions = showAdd
    ? [
        {
          id: 'new-reward',
          label: 'New reward',
          icon: <Plus className="size-4" />,
          variant: 'primary' as const,
          onClick: () => navigate('/v2/performance/rewards/new'),
        },
      ]
    : [];

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PageHeader
        title="Rewards"
        actions={headerActions}
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <div className="flex-1 overflow-auto px-6 py-6">
        {showAdd && (
          <div className="flex justify-end mb-6">
            <Button
              variant="outline"
              onClick={() => navigate('/v2/performance/rewards/archive')}
              className="gap-2"
            >
              <Archive className="size-4" />
              Archive
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && myRewards.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <p className="text-lg font-bold text-muted-foreground">
              You have no rewards
            </p>
          </div>
        )}

        {!loading && myRewards.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {myRewards.map((circle) =>
              circle.rewards.map((reward) => {
                if (reward.remaingDaysAvailable < 0) {
                  return null;
                }
                const canEdit =
                  hasUpdatePermission &&
                  (global.me.isAdmin || reward.createdBy === global.me.id);

                return (
                  <RewardCard
                    key={reward.id}
                    reward={reward}
                    onEdit={handleEditReward}
                    canEdit={!!canEdit}
                    onClick={() => handleRewardClick(reward)}
                  />
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatPoints(number: number) {
  if (number <= 0) return '';
  if (number === 1) {
    return number + ' pt';
  } else {
    return number + ' pts';
  }
}
